import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { App, Badge, Button, Card, Space, Typography } from "antd";
import type { UploadFile } from "antd";
import { CloudUploadOutlined, PaperClipOutlined } from "@ant-design/icons";
import { Attachments, Bubble, Sender } from "@ant-design/x";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import agencyIntakeSchema from "../data/agency_intake_schema.json";
import { deleteProject } from "../lib/api";
import { APP_BRAND_NAME } from "../lib/brand";
import { markdownComponents } from "./MarkdownLink";

interface UploadedFile {
  uid: string;
  file: File;
  content: string;
  type: string;
}

interface ChatPanelProps {
  title: string;
  description: string;
  placeholder: string;
  seedMessages?: ChatMessage[];
  seedToken?: number;
  openingPrompt?: string | null;
  showDeleteLastProject?: boolean;
  showUndo?: boolean;
  requireToolConfirmation?: boolean;
  onSend?: (
    message: string,
    handlers: {
      onChunk: (chunk: string) => void;
      onThought: (chunk: string) => void;
      onMeta: (meta: any) => void;
      onConfirmationRequired?: (confirmation: any) => Promise<void>;
    },
    history: ChatMessage[],
    files?: UploadedFile[],
    extraOptions?: {
      confirmedTool?: { name: string; args: any };
      sessionId?: string;
    }
  ) => Promise<string | undefined | void>;
}

interface ChatMessage {
  role: "assistant" | "user";
  text: string;
  thoughts?: string;
  attachments?: UploadFile[];
  streaming?: boolean;
  confirmation?: {
    tool: string;
    args: Record<string, any>;
    message: string;
  };
}

export default function ChatPanel({
  title,
  description,
  placeholder,
  seedMessages,
  seedToken,
  openingPrompt: openingPromptProp,
  requireToolConfirmation = false,
  showDeleteLastProject = true,
  showUndo = true,
  onSend,
}: ChatPanelProps) {
  const firstQuestion =
    agencyIntakeSchema?.find((item) => !item.internal)?.question ||
    "What problem are you trying to solve?";
  const defaultOpeningPrompt = `Hi! I'm ${APP_BRAND_NAME}, and I'm here to guide your AI project intake. ${firstQuestion}`;
  const initialMessages: ChatMessage[] =
    openingPromptProp === undefined
      ? [{ role: "assistant", text: defaultOpeningPrompt }]
      : openingPromptProp
        ? [{ role: "assistant", text: openingPromptProp }]
        : [];
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<UploadFile[]>([]);
  const [headerOpen, setHeaderOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [assistantTyping, setAssistantTyping] = useState(false);
  const [progressState, setProgressState] = useState<
    | {
        step: number;
        total: number;
        label: string;
        missing?: string[];
      }
    | null
  >(null);
  const [undoStack, setUndoStack] = useState<ChatMessage[][]>([]);
  const [lastCreatedProject, setLastCreatedProject] = useState<{ id: string; name?: string } | null>(null);
  const [toolConfirmation, setToolConfirmation] = useState<{
    visible: boolean;
    toolName: string;
    toolArgs: any;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
  } | null>(null);
  const pendingConfirmationRef = useRef(false);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const senderContainerRef = useRef<HTMLDivElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  const { message } = App.useApp();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const node = senderContainerRef.current?.querySelector("textarea, input") as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null;
    if (node && !node.id) {
      node.id = "chat-panel-textarea";
      if (!node.getAttribute("aria-label")) {
        node.setAttribute("aria-label", "Chat message");
      }
    }
  });

  // Detect prefers-reduced-motion for accessibility
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Announce new messages to screen readers
  const announceMessage = useCallback((role: string, text: string) => {
    if (liveRegionRef.current) {
      const announcement = `${role === "assistant" ? APP_BRAND_NAME : "You"}: ${text.slice(0, 100)}${text.length > 100 ? "..." : ""}`;
      liveRegionRef.current.textContent = announcement;

      // Clear after announcement is read
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = "";
        }
      }, 3000);
    }
  }, []);

  useEffect(() => {
    const container = messagesRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    if (!seedMessages || seedMessages.length === 0) return;
    setMessages(seedMessages);
  }, [seedMessages, seedToken]);

  useEffect(() => {
    const lastAssistantMessage = [...messages].reverse().find((msg) => msg.role === "assistant");
    if (!lastAssistantMessage?.text) return;

    const match = lastAssistantMessage.text.match(/Missing details:\s*(.+)/i);
    if (match) {
      const missing = match[1]
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      setProgressState({
        step: 2,
        total: 4,
        label: "Waiting on project details",
        missing,
      });
    }
  }, [messages]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        const input = senderContainerRef.current?.querySelector(
          "textarea, input"
        ) as HTMLInputElement | HTMLTextAreaElement | null;
        input?.focus();
      }

      if (event.key === "Escape") {
        const input = senderContainerRef.current?.querySelector(
          "textarea, input"
        ) as HTMLInputElement | HTMLTextAreaElement | null;
        input?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleFileChange = async (info: { fileList: UploadFile[] }) => {
    const MAX_FILES = 5;
    const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

    if (info.fileList.length > MAX_FILES) {
      message.error(`Limit ${MAX_FILES} files per turn.`);
      info.fileList = info.fileList.slice(0, MAX_FILES);
    }

    setAttachedFiles(info.fileList);

    // Process files for content extraction
    const processedFiles: UploadedFile[] = [];

    for (const fileItem of info.fileList) {
      const file = fileItem.originFileObj;
      if (!file) continue;

      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      const isSupported =
        fileType.startsWith("image/") ||
        fileType === "application/pdf" ||
        fileType === "text/plain" ||
        fileType === "text/markdown" ||
        fileType === "text/csv" ||
        fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        fileName.endsWith(".pdf") ||
        fileName.endsWith(".txt") ||
        fileName.endsWith(".md") ||
        fileName.endsWith(".csv") ||
        fileName.endsWith(".docx");

      if (!isSupported) {
        message.error(`File type not supported: ${file.name}`);
        continue;
      }

      if (file.size > MAX_BYTES) {
        message.error(`${file.name} exceeds 10 MB limit.`);
        continue;
      }

      try {
        const content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === "string") {
              const dataUrl = reader.result;
              const parts = dataUrl.split(",");
              resolve(parts.length === 2 ? parts[1] : dataUrl);
              return;
            }
            const buffer = reader.result as ArrayBuffer;
            const bytes = new Uint8Array(buffer);
            let binary = "";
            for (let i = 0; i < bytes.length; i += 1) {
              binary += String.fromCharCode(bytes[i]);
            }
            resolve(btoa(binary));
          };
          reader.onerror = reject;
          if (fileType.startsWith("image/")) {
            reader.readAsDataURL(file);
          } else {
            reader.readAsArrayBuffer(file);
          }
        });

        processedFiles.push({
          uid: fileItem.uid,
          file,
          content,
          type: fileType || "application/octet-stream",
        });
      } catch (error) {
        console.error(`Error reading file ${file.name}:`, error);
        message.error(`Failed to read file: ${file.name}`);
      }
    }

    setUploadedFiles(processedFiles);
  };

  const handleSendMessage = async (userMessage: string) => {
    const trimmedMessage = userMessage.trim();
    const hasText = Boolean(trimmedMessage);
    const hasAttachments = attachedFiles.length > 0;
    if ((!hasText && !hasAttachments) || sending) return;
    const outgoingMessage = hasText ? trimmedMessage : "Please review the attached file(s).";
    const normalizedOutgoing = outgoingMessage.toLowerCase();
    const isProjectCreationIntent = /\b(create|start|new)\b.*\bproject\b/.test(normalizedOutgoing) ||
      normalizedOutgoing.startsWith("/create-project");

    // Add user message and clear input immediately
    const userAttachments = hasAttachments ? attachedFiles.map((file) => ({ ...file })) : undefined;
    // Don't add to undo stack yet - we'll do it when a write operation completes
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        text: userMessage,
        attachments: userAttachments,
      },
    ]);
    setInputValue("");
    announceMessage("user", userMessage);

    if (isProjectCreationIntent) {
      setProgressState({ step: 1, total: 4, label: "Gathering project details" });
    }

    if (!onSend) return;
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setSending(true);
    setAssistantTyping(true);
    let assistantIndex = -1;
    let hasContent = false;
    let receivedMeta = false;

    const resolvedMessage = (() => {
      if (outgoingMessage.startsWith("/list-projects")) return "List the most recent projects.";
      if (outgoingMessage.startsWith("/search-vendors")) return "List active vendors and their capabilities.";
      if (outgoingMessage.startsWith("/create-project")) {
        const details = outgoingMessage.replace("/create-project", "").trim();
        return details ? `Create a project with these details: ${details}` : "Create a new project.";
      }
      return outgoingMessage;
    })();
    
    try {
      const historyForSend: ChatMessage[] = [
        ...messages,
        { role: "user", text: resolvedMessage, attachments: userAttachments },
      ];
      
      // Handler for confirmation requests from backend
      const handleConfirmationRequired = async (confirmation: any) => {
        if (!requireToolConfirmation) {
          // If confirmation not required by UI, proceed automatically
          return;
        }

        pendingConfirmationRef.current = true;
        setSending(false);
        setAssistantTyping(false);

        const toolName = confirmation.tool || "unknown";
        const toolArgs = confirmation.args || {};
        const confirmMessage = confirmation.message || `Execute ${toolName}?`;

        setToolConfirmation({
          visible: true,
          toolName,
          toolArgs,
          message: confirmMessage,
          onConfirm: async () => {
            setToolConfirmation(null);
            pendingConfirmationRef.current = false;

            // User confirmed - send a new request with the confirmed tool
            // This is a NEW request, not a continuation of the hanging connection
            setSending(true);
            setAssistantTyping(true);
            let confirmAssistantIndex = -1;
            let confirmHasContent = false;

            try {
              await onSend(
                "", // Empty prompt - we're just confirming
                {
                  onChunk: (chunk) => {
                    if (!confirmHasContent) {
                      setMessages((prev) => {
                        confirmAssistantIndex = prev.length;
                        return [...prev, { role: "assistant", text: chunk, streaming: true }];
                      });
                      confirmHasContent = true;
                      setAssistantTyping(false);
                    } else {
                      setMessages((prev) =>
                        prev.map((item, index) =>
                          index === confirmAssistantIndex
                            ? { ...item, text: `${item.text}${chunk}`, streaming: true }
                            : item
                        )
                      );
                    }
                  },
                  onThought: (chunk) => {
                    if (!confirmHasContent) {
                      setMessages((prev) => {
                        confirmAssistantIndex = prev.length;
                        return [
                          ...prev,
                          {
                            role: "assistant",
                            text: "",
                            thoughts: chunk,
                            streaming: true,
                          },
                        ];
                      });
                      confirmHasContent = true;
                      setAssistantTyping(false);
                    } else {
                      setMessages((prev) =>
                        prev.map((item, index) =>
                          index === confirmAssistantIndex
                            ? {
                                ...item,
                                thoughts: `${item.thoughts ?? ""}${chunk}`,
                                streaming: true,
                              }
                            : item
                        )
                      );
                    }
                  },
                  onMeta: (meta: any) => {
                    if (!meta || typeof meta !== "object") return;

                    const writeTools = [
                      "create_project",
                      "update_project",
                      "create_vendor",
                      "update_intake_status",
                      "add_intake_comment",
                      "convert_intake_to_project",
                      "create_intake",
                      "update_intake",
                    ];
                    if (meta.tool && writeTools.includes(meta.tool)) {
                      setUndoStack((prev) => [...prev, messages]);
                    }

                    if (meta.tool === "create_project") {
                      setLastCreatedProject({ id: meta.id, name: meta.name });
                      setProgressState({ step: 4, total: 4, label: "Project created" });
                      message.success({
                        content: (
                          <span>
                            Project created{meta.name ? `: ${meta.name}` : ""}.{" "}
                            <Typography.Link
                              href="/projects"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate("/projects");
                              }}
                            >
                              View projects
                            </Typography.Link>
                          </span>
                        ),
                      });
                    }

                    if (meta.tool === "update_project") {
                      message.success("Project updated successfully.");
                    }

                    if (meta.tool === "create_vendor") {
                      message.success({
                        content: (
                          <span>
                            Vendor created{meta.name ? `: ${meta.name}` : ""}.{" "}
                            <Typography.Link
                              href="/vendors"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate("/vendors");
                              }}
                            >
                              View vendors
                            </Typography.Link>
                          </span>
                        ),
                      });
                    }
                  },
                },
                historyForSend,
                undefined,
                {
                  confirmedTool: {
                    name: confirmation.tool,
                    args: confirmation.args,
                  },
                  sessionId: confirmation.sessionId,
                }
              );

              if (confirmHasContent && confirmAssistantIndex !== -1) {
                setMessages((prev) =>
                  prev.map((item, index) => {
                    if (index === confirmAssistantIndex) {
                      announceMessage("assistant", item.text);
                      return { ...item, streaming: false };
                    }
                    return item;
                  })
                );
              }
            } catch (error) {
              console.error("Error confirming tool:", error);
              message.error("Failed to execute confirmed action.");
            } finally {
              setSending(false);
              setAssistantTyping(false);
            }
          },
          onCancel: () => {
            setToolConfirmation(null);
            pendingConfirmationRef.current = false;
            message.info("Action cancelled by user.");
            setMessages((prev) => [
              ...prev,
              { role: "assistant", text: "Action cancelled.", streaming: false },
            ]);
          },
        });

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: "",
            confirmation: {
              tool: toolName,
              args: toolArgs,
              message: confirmMessage,
            },
          },
        ]);

      };

      
      const sendHandlers = {
        onChunk: (chunk: string) => {
          // Add assistant message on first chunk
          if (!hasContent) {
            setMessages((prev) => {
              assistantIndex = prev.length;
              return [...prev, { role: "assistant", text: chunk, streaming: true }];
            });
            hasContent = true;
            setAssistantTyping(false);
          } else {
            setMessages((prev) =>
              prev.map((item, index) =>
                index === assistantIndex
                  ? { ...item, text: `${item.text}${chunk}`, streaming: true }
                  : item
              )
            );
          }
        },
        onThought: (chunk: string) => {
          if (!hasContent) {
            setMessages((prev) => {
              assistantIndex = prev.length;
              return [
                ...prev,
                {
                  role: "assistant",
                  text: "",
                  thoughts: chunk,
                  streaming: true,
                },
              ];
            });
            hasContent = true;
            setAssistantTyping(false);
          } else {
            setMessages((prev) =>
              prev.map((item, index) =>
                index === assistantIndex
                  ? {
                      ...item,
                      thoughts: `${item.thoughts ?? ""}${chunk}`,
                      streaming: true,
                    }
                  : item
              )
            );
          }
        },
        onMeta: (meta: any) => {
          if (!meta || typeof meta !== "object") return;
          receivedMeta = true;

          // Track undo state for write operations only
          const writeTools = [
            "create_project",
            "update_project",
            "create_vendor",
            "update_intake_status",
            "add_intake_comment",
            "convert_intake_to_project",
            "create_intake",
            "update_intake",
          ];
          if (meta.tool && writeTools.includes(meta.tool)) {
            setUndoStack((prev) => [...prev, messages]);
          }

          if (meta.tool === "create_project") {
            setLastCreatedProject({ id: meta.id, name: meta.name });
            setProgressState({ step: 4, total: 4, label: "Project created" });
            message.success({
              content: (
                <span>
                  Project created{meta.name ? `: ${meta.name}` : ""}.{" "}
                  <Typography.Link
                              href="/projects"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate("/projects");
                              }}
                            >
                              View projects
                            </Typography.Link>
                </span>
              ),
            });
          }

          if (meta.tool === "update_project") {
            message.success("Project updated successfully.");
          }

          if (meta.tool === "create_vendor") {
            message.success({
              content: (
                <span>
                  Vendor created{meta.name ? `: ${meta.name}` : ""}.{" "}
                  <Typography.Link
                              href="/vendors"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate("/vendors");
                              }}
                            >
                              View vendors
                            </Typography.Link>
                </span>
              ),
            });
          }
        },
        onConfirmationRequired: handleConfirmationRequired,
      };

      await onSend(
        resolvedMessage,
        sendHandlers,
        historyForSend,
        uploadedFiles.length > 0 ? uploadedFiles : undefined
      );

      // Clear uploaded files after sending
      setUploadedFiles([]);
      setAttachedFiles([]);
      setHeaderOpen(false);

      if (!hasContent && !receivedMeta && !pendingConfirmationRef.current) {
        message.error("Streaming response required. Please try again.");
      } else if (assistantIndex !== -1) {
        setMessages((prev) =>
          prev.map((item, index) => {
            if (index === assistantIndex) {
              announceMessage("assistant", item.text);
              return { ...item, streaming: false };
            }
            return item;
          })
        );
      }
    } finally {
      setSending(false);
      setAssistantTyping(false);
    }
  };

  // Attach button with badge
  const attachmentsNode = (
    <Badge dot={attachedFiles.length > 0 && !headerOpen}>
      <Button
        type="text"
        icon={<PaperClipOutlined />}
        onClick={() => setHeaderOpen(!headerOpen)}
      />
    </Badge>
  );

  // Sender header for attachments
  const senderHeader = (
    <Sender.Header
      title="Attachments"
      open={headerOpen}
      onOpenChange={setHeaderOpen}
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <Attachments
        beforeUpload={() => false}
        items={attachedFiles}
        onChange={handleFileChange}
        accept=".pdf,.docx,.txt,.md,.csv,image/png,image/jpeg,image/webp"
        placeholder={(type) =>
          type === "drop"
            ? {
                title: "Drop file here",
              }
            : {
                icon: <CloudUploadOutlined />,
                title: "Upload files",
                description: "Click or drag files to this area to upload",
              }
        }
      />
    </Sender.Header>
  );

  const slashCommands = [
    { value: "/create-project", label: "Create project", description: "Start a new project" },
    { value: "/search-vendors", label: "Search vendors", description: "Find vendors and capabilities" },
    { value: "/list-projects", label: "List projects", description: "Show recent projects" },
  ];

  const filteredSlashCommands = inputValue.startsWith("/")
    ? slashCommands.filter((cmd) => cmd.value.startsWith(inputValue))
    : [];

  const handleUndo = () => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const last = next.pop();
      if (last) setMessages(last);
      return next;
    });
  };

  const handleDeleteLastProject = async () => {
    if (!lastCreatedProject) return;
    try {
      await deleteProject(lastCreatedProject.id);
      message.success("Project deleted.");
      setLastCreatedProject(null);
    } catch (error) {
      console.error("Failed to delete project:", error);
      message.error("Failed to delete project.");
    }
  };

  return (
    <Card>
      {/* Hidden live region for screen reader announcements */}
      <div
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          left: "-10000px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      />
      
      
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Space align="center" size="middle" style={{ width: "100%", justifyContent: "space-between" }}>
            <div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {title}
              </Typography.Title>
              <Typography.Text type="secondary">{description}</Typography.Text>
            </div>
            <Space>
              {showUndo ? (
                <Button onClick={handleUndo} disabled={undoStack.length === 0}>
                  Undo last action
                </Button>
              ) : null}
              {showDeleteLastProject ? (
                <Button onClick={handleDeleteLastProject} disabled={!lastCreatedProject}>
                  Delete last project
                </Button>
              ) : null}
            </Space>
          </Space>
          {progressState ? (
            <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              {`Step ${progressState.step} of ${progressState.total}: ${progressState.label}`}
              {progressState.missing?.length
                ? ` • Missing: ${progressState.missing.join(", ")}`
                : ""}
            </Typography.Text>
          ) : null}
        </div>

        <div
          ref={messagesRef}
          style={{ height: 400, overflowY: "auto", width: "100%" }}
          role="log"
          aria-live="polite"
          aria-relevant="additions text"
          aria-label="Chat messages"
        >
          <Bubble.List
            role={{
              ai: {
                placement: "start",
                typing: prefersReducedMotion ? false : { effect: "typing", step: 4 },
                contentRender: (content, info) => {
                  const attachments = info?.extraInfo?.attachments as UploadFile[] | undefined;
                  const thoughts = info?.extraInfo?.thoughts as string | undefined;
                  const confirmation = info?.extraInfo?.confirmation as ChatMessage["confirmation"] | undefined;
                  const isConfirmationActive =
                    Boolean(confirmation) &&
                    toolConfirmation?.visible &&
                    toolConfirmation?.toolName === confirmation?.tool;
                  return (
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      {attachments?.length ? (
                        <Attachments
                          items={attachments}
                          disabled
                          styles={{
                            upload: {
                              display: "none",
                            },
                          }}
                        />
                      ) : null}
                      {thoughts ? (
                        <Typography.Text
                          style={{
                            display: "block",
                            fontSize: 12,
                            fontStyle: "italic",
                            color: "rgba(0, 0, 0, 0.45)",
                            whiteSpace: "pre-wrap",
                            WebkitMaskImage:
                              "linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0) 100%)",
                            maskImage:
                              "linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0) 100%)",
                          }}
                        >
                          {thoughts}
                        </Typography.Text>
                      ) : null}
                      {confirmation ? (
                        <Card size="small" style={{ maxWidth: 520 }}>
                          <Space direction="vertical" size={8} style={{ width: "100%" }}>
                            <Typography.Text strong>{confirmation.message}</Typography.Text>
                            <div style={{ maxHeight: 220, overflow: "auto", paddingRight: 8 }}>
                              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                {Object.entries(confirmation.args || {}).map(([key, value]) => {
                                  if (key === "summaryAnswers" && Array.isArray(value)) {
                                    return (
                                      <div key={key}>
                                        <Typography.Text strong>Summary Answers</Typography.Text>
                                        <ul style={{ margin: "6px 0 0 16px" }}>
                                          {value.map((item: any, index: number) => (
                                            <li key={`${key}-${index}`}>
                                              <Typography.Text>
                                                <strong>{item?.question || "Question"}:</strong> {item?.answer || ""}
                                              </Typography.Text>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    );
                                  }

                                  const displayValue =
                                    typeof value === "string"
                                      ? value
                                      : typeof value === "number" || typeof value === "boolean"
                                        ? String(value)
                                        : Array.isArray(value)
                                          ? value.join(", ")
                                          : value && typeof value === "object"
                                            ? JSON.stringify(value)
                                            : "";

                                  return (
                                    <Typography.Text key={key}>
                                      <strong>{key}:</strong> {displayValue}
                                    </Typography.Text>
                                  );
                                })}
                              </Space>
                            </div>
                            <Space>
                              <Button
                                size="small"
                                type="primary"
                                onClick={toolConfirmation?.onConfirm}
                                loading={sending}
                                disabled={!isConfirmationActive}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="small"
                                type="default"
                                danger
                                onClick={toolConfirmation?.onCancel}
                                disabled={!isConfirmationActive}
                              >
                                Cancel
                              </Button>
                            </Space>
                          </Space>
                        </Card>
                      ) : null}
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
                        {String(content ?? "")}
                      </ReactMarkdown>
                    </Space>
                  );
                },
                styles: {
                  content: {
                    borderRadius: 16,
                  },
                },
              },
              user: {
                placement: "end",
                variant: "shadow",
                contentRender: (content, info) => {
                  const attachments = info?.extraInfo?.attachments as UploadFile[] | undefined;
                  return (
                    <Space direction="vertical" size={8}>
                      {attachments?.length ? (
                        <Attachments
                          items={attachments}
                          disabled
                          styles={{
                            upload: {
                              display: "none",
                            },
                          }}
                        />
                      ) : null}
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
                        {String(content ?? "")}
                      </ReactMarkdown>
                    </Space>
                  );
                },
                styles: {
                  content: {
                    borderRadius: 16,
                  },
                },
              },
            }}
            items={messages.map((msg, index) => ({
              key: `msg-${index}`,
              role: msg.role === "user" ? ("user" as const) : ("ai" as const),
              content: msg.text,
              streaming: Boolean(msg.streaming),
              extraInfo: { attachments: msg.attachments, thoughts: msg.thoughts, confirmation: msg.confirmation },
            }))}
          />
          {assistantTyping ? (
            <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              {APP_BRAND_NAME} is thinking...
            </Typography.Text>
          ) : null}
        </div>

        {filteredSlashCommands.length ? (
          <Card size="small" style={{ marginBottom: 8 }}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {filteredSlashCommands.map((cmd) => (
                <Button
                  key={cmd.value}
                  type="text"
                  onClick={() => setInputValue(cmd.value + " ")}
                  style={{ textAlign: "left" }}
                >
                  <strong>{cmd.label}</strong> — {cmd.description}
                </Button>
              ))}
            </Space>
          </Card>
        ) : null}

        <div
          ref={senderContainerRef}
          className="chat-panel-sender"
          role="group"
          aria-label="Message composer"
        >
          <style>{`
            .chat-panel-sender textarea,
            .chat-panel-sender input {
              outline: none;
            }
            .chat-panel-sender textarea:focus-visible,
            .chat-panel-sender input:focus-visible,
            .chat-panel-sender button:focus-visible {
              outline: 3px solid #1E79C8;
              outline-offset: 2px;
              border-radius: 4px;
            }
          `}</style>
          <label htmlFor="chat-panel-textarea" style={{ position: "absolute", left: -10000, width: 1, height: 1, overflow: "hidden" }}>
            {placeholder || "Chat message"}
          </label>
          <Sender
            value={inputValue}
            onChange={setInputValue}
            placeholder={placeholder}
            header={senderHeader}
            onSubmit={handleSendMessage}
            loading={sending}
            prefix={attachmentsNode}
            style={{ boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}
          />
        </div>
      </Space>
    </Card>
  );
}
